import React, { useEffect, useRef, useState } from "react";
import { ChartColumn, CircleX, Loader2, ReceiptText } from "lucide-react";
import { AlertDialog } from "@/components/ui/alert-dialog";
import AlertMessageDialog from "@/components/custom-ui/result-dialog";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { isArray } from "lodash";

import {
  useCareHistoryClaimsQuery,
  useCareHistoryConfigQuery,
  useCareHistoryGraphQuery,
  useDownloadClaims,
} from "@/hooks/use-care-history";
import PageContainer from "./containers/page-container";
import {
  ACCUMULATOR_YEAR_DROPDOWN_OPTIONS,
  CARE_HISTORY_PERIOD_FLAG,
  CARE_HISTORY_PERIOD_TYPE,
  CLAIM_TIME_PERIOD,
  CLAIM_TYPE_DROPDOWN_OPTIONS,
  CLAIM_TYPES,
  CLAIMS_FILTER_OPTIONS,
  CLAIMS_SORT_BY,
  CLAIMS_SORT_OPTIONS,
  COVERAGE_CODE,
  EOB_VERBIAGE,
  PLAN_YEAR_DROPDOWN_OPTIONS,
  QueryKeys,
} from "@/constants";
import type { Relation } from "@/model/demographics";
import type {
  CareHistoryFilterStateType,
  ClaimFilterDropDownType,
  ClaimsProcedure,
  OptionType,
} from "@/types";
import SingleSelect from "./components/single-slect";
import RightSidebar from "./components/right-sidebar";
import type { CareHistoryClaimsResponseType } from "@/model/care-history";
import VisitSummary from "./components/care-history/visit-summary";
import Collapsible from "./components/collapsible";
import AutoComplete from "./components/dataset-search-autocomplete";
import Dropdown from "./components/drop-down";
import VisitDetailsModal from "./components/care-history/visit-details-modal";
import ClaimsSkeletonLoader from "./components/care-history/claims-skeleton-loader";
import GraphSkeletonLoader from "./components/care-history/graph-skeleton-loader";

import {
  filterByAdvancedSearch,
  filterByClaimNumber,
  getDisabledStatus,
  getLegendItems,
  searchCareEntity,
  searchClaimNumbers,
  searchDrugName,
  searchPharmacy,
  searchProcedure,
  normalizeCareHistoryServiceDateRange,
} from "@/lib/care-history-utils";
import type { DataAccessListItem } from "@/model/data-access";
import useUserInfo from "@/hooks/use-user-info";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { NoDataAvailable, SomethingWentWrong } from "@/pages/components/empty";
import { useFeatureName } from "@/hooks/use-pageTitle";
import InputDate from "./components/input-date";
import {
  LogCategories,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";
import IDENTIFIERS from "@/constants/test_id";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Title } from "@/components/ui/title";
import { Label } from "@/components/ui/label";
import { useCachedQueryData } from "@/hooks/use-cached-query-data";
import type { GlobalFlag } from "@/model/global-flag";
import { queryClient } from "@/hooks/use-query-client";

const CareHistory: React.FunctionComponent = () => {
  const featureName = useFeatureName();
  const { activeCoverage } = useSelector(
    (state: RootState) => state?.planCoverageInfo,
  );
  const DEFAULT_FILTERS = {
    selectedFilterOption: CLAIMS_FILTER_OPTIONS.CLAIM_NUMBER.VALUE,
    selectedClaimForClaimNumber: null,
    selectedClaimTimePeriod: CLAIM_TIME_PERIOD.ALL,
    selectedMembers: [],
    selectedClaimType: CLAIM_TYPES.ALL,
    selectedCareEntity: "",
    selectedProcedure: null,
    selectedPharmacy: "",
    selectedDrugName: "",
    sortClaimsBy: {
      LABEL: CLAIMS_SORT_BY.DATE_OF_SERVICE_NEW_TO_OLD.LABEL,
      VALUE: CLAIMS_SORT_BY.DATE_OF_SERVICE_NEW_TO_OLD.VALUE,
    },
    selectedDateRange: {
      fromDate: null,
      toDate: null,
    },
  };

  const userInfo = useUserInfo();
  // Reactive reads: these entries are populated asynchronously via setQueryData
  // (from the demographics query and the separate data-access call), so we must
  // subscribe to them rather than snapshot once with getQueryData. The raw
  // values are referentially stable and used as effect dependencies below.
  const membersData = useCachedQueryData<Record<string, Relation>>([
    QueryKeys.MEMBERS,
  ]);
  const dependentsData = useCachedQueryData<Relation[]>([QueryKeys.DEPENDENTS]);
  const authorizationAccessList = useCachedQueryData<DataAccessListItem[]>([
    QueryKeys.DATA_ACCESS_LIST,
  ]);
  //global flag
  const showCareHistoryGraph =
    (
      queryClient.getQueryData([QueryKeys.GLOBAL_FLAGS, userInfo.username]) as
        | { data?: GlobalFlag[] }
        | undefined
    )?.data?.[0]?.showCareHistoryGraph ?? true;

  const members = membersData ?? {};
  const dependents = dependentsData ?? [];
  const [selectedPeriod, setSelectedPeriod] = useState<string>(
    CARE_HISTORY_PERIOD_FLAG.MONTHLY,
  );
  const [visitDetailsSideBar, setVisitDetailsSideBar] = useState<{
    selectedVisitSummary?: CareHistoryClaimsResponseType | null;
    isOpen: boolean;
  }>({
    isOpen: false,
    selectedVisitSummary: null,
  });
  const [showFilterSideBar, setShowFilterSideBar] = useState<boolean>(false);
  const [eobError, setEobError] = useState(false);
  const [preventClose, setPreventClose] = useState<boolean>(false);

  const [filters, setFilters] =
    useState<CareHistoryFilterStateType>(DEFAULT_FILTERS);

  const { mutate: downloadClaims, isPending: isDownloadClaimsPending } =
    useDownloadClaims();
  const [downloadClaimsErrorMsg, setDownloadClaimsErrorMsg] = useState<
    string | null
  >(null);
  const { data: config } = useCareHistoryConfigQuery();
  const graphFromDate =
    selectedPeriod === CARE_HISTORY_PERIOD_FLAG.MONTHLY
      ? config?.p1StartDate
      : config?.planStartDate;
  const graphToDate =
    selectedPeriod === CARE_HISTORY_PERIOD_FLAG.MONTHLY
      ? config?.p1EndDate
      : config?.planEndDate;
  const claimsFromDate =
    selectedPeriod === CARE_HISTORY_PERIOD_FLAG.MONTHLY
      ? config?.periodStartDate
      : config?.planStartDate;
  const claimsToDate =
    selectedPeriod === CARE_HISTORY_PERIOD_FLAG.MONTHLY
      ? config?.periodEndDate
      : config?.planEndDate;

  const serviceDateRange = normalizeCareHistoryServiceDateRange({
    claimsFromDate,
    claimsToDate,
    planStartDate: config?.planStartDate,
    planEndDate: config?.planEndDate,
    fromDate: filters?.selectedDateRange?.fromDate ?? null,
    toDate: filters?.selectedDateRange?.toDate ?? null,
  });

  const effectiveMaxDate = serviceDateRange.effectiveMaxEnd;
  const fromDate = serviceDateRange.uiFrom;
  const toDate = serviceDateRange.uiTo;
  const serviceStartMax = toDate; // normalized to ensure from <= to

  const {
    data: graphData,
    isFetching: isGraphFetching,
    isFetched: isGraphFetched,
    isError: isGraphError,
  } = useCareHistoryGraphQuery(
    graphFromDate!,
    graphToDate!,
    selectedPeriod,
    activeCoverage,
  );
  const {
    data: claimsData,
    isFetching: isClaimsFetching,
    isFetched: isClaimsFetched,
    isError: isClaimsError,
  } = useCareHistoryClaimsQuery(
    claimsFromDate!,
    claimsToDate!,
    selectedPeriod,
    activeCoverage,
  );

  // Tracks whether the user has manually chosen members via the filter panel,
  // so late-arriving data doesn't clobber their selection.
  const hasUserSelectedMembers = useRef(false);

  // Full reset (including the default member selection) when coverage changes.
  useEffect(() => {
    hasUserSelectedMembers.current = false;
    setFilters({
      ...DEFAULT_FILTERS,
      selectedMembers: getAllPHIVisibleMbrID(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCoverage.claimCoverageCode]);

  // Members, dependents and the data-access list arrive asynchronously and at
  // different times. Recompute the default member selection as they land so the
  // claims list is filtered on load (previously this only happened when the
  // filter panel was opened). Stops once the user makes a manual selection.
  useEffect(() => {
    if (hasUserSelectedMembers.current) return;
    setFilters((prev) => ({
      ...prev,
      selectedMembers: getAllPHIVisibleMbrID(),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membersData, dependentsData, authorizationAccessList]);

  useEffect(() => {
    setFilters((prev) => {
      if (prev.selectedClaimTimePeriod !== "Date of Service") return prev;
      return {
        ...prev,
        selectedDateRange: {
          fromDate: null,
          toDate: null,
        },
      };
    });
  }, [selectedPeriod]);

  function onDownloadClaims() {
    if (!claimsFromDate || !claimsToDate) return;
    downloadClaims(
      {
        fromDate: claimsFromDate,
        toDate: claimsToDate,
        periodFlag: selectedPeriod,
      },
      {
        onSuccess: (data) => {
          const { success, message } = data as {
            success: boolean;
            message: string;
          };
          if (!success) {
            setDownloadClaimsErrorMsg(
              message ?? "Failed to download claims. Please try again.",
            );
          }
        },
        onError: (error: Error) => {
          setDownloadClaimsErrorMsg(
            error?.message ?? "Failed to download claims. Please try again.",
          );
        },
      },
    );
  }

  const getAllPHIVisibleMbrID = (): string[] => {
    if (!dependents || !members || !userInfo?.memberId) {
      return [];
    }

    const primaryMember = members[userInfo?.memberId];
    if (!primaryMember) return [];

    return [...dependents, primaryMember]
      .filter(
        (m) =>
          m?.memberId &&
          !getDisabledStatus(
            m,
            authorizationAccessList as DataAccessListItem[],
            userInfo.memberId,
            userInfo.relationship,
          ),
      )
      .map((m) => m.memberId)
      .filter(Boolean);
  };

  const onVisitDetailsSideBarClose = () => {
    setVisitDetailsSideBar({
      isOpen: false,
      selectedVisitSummary: null,
    });
  };

  const onVisitSummaryClick = (
    selectedVisitSummary: CareHistoryClaimsResponseType,
  ) => {
    setVisitDetailsSideBar({
      isOpen: true,
      selectedVisitSummary,
    });
  };

  /**
   * Handles closing the filter sidebar.
   * If `preventClose` is true, the sidebar should remain open (e.g., when interacting with a calendar popup),
   * and we reset `preventClose` for the next close attempt.
   * Otherwise, close the sidebar normally.
   */
  const onFilterSideBarClose = () => {
    if (preventClose) {
      setPreventClose(false);
      return;
    }
    setShowFilterSideBar(false);
  };

  const onClaimNumberSelect = (
    item: CareHistoryClaimsResponseType | string | ClaimsProcedure,
  ) => {
    setFilters({
      ...filters,
      selectedClaimForClaimNumber: item as CareHistoryClaimsResponseType,
    });
  };

  const onCareEntitySelect = (
    item: CareHistoryClaimsResponseType | string | ClaimsProcedure,
  ) => {
    setFilters({ ...filters, selectedCareEntity: item as string });
  };

  const onProcedureSelect = (
    item: CareHistoryClaimsResponseType | string | ClaimsProcedure,
  ) => {
    setFilters({ ...filters, selectedProcedure: item as ClaimsProcedure });
  };

  const onPharmacySelect = (
    item: CareHistoryClaimsResponseType | string | ClaimsProcedure,
  ) => {
    setFilters({ ...filters, selectedPharmacy: item as string });
  };

  const onDrugNameSelect = (
    item: CareHistoryClaimsResponseType | string | ClaimsProcedure,
  ) => {
    setFilters({ ...filters, selectedDrugName: item as string });
  };

  const renderClaimNumbers = (
    claim: CareHistoryClaimsResponseType | string | ClaimsProcedure,
  ): React.ReactNode => (claim as CareHistoryClaimsResponseType).revClaimId;

  const renderCareEntity = (
    claim: CareHistoryClaimsResponseType | string | ClaimsProcedure,
  ): React.ReactNode => claim as string;

  const renderProcedure = (
    claim: ClaimsProcedure | CareHistoryClaimsResponseType | string,
  ): React.ReactNode =>
    `${(claim as ClaimsProcedure).procedureDesc} - ${(claim as ClaimsProcedure).procedureCode}`;

  const renderPharmacy = (
    claim: CareHistoryClaimsResponseType | string | ClaimsProcedure,
  ): React.ReactNode => claim as string;

  const renderDrugName = (
    claim: CareHistoryClaimsResponseType | string | ClaimsProcedure,
  ): React.ReactNode => claim as string;

  const renderClaimsLabel = () => {
    if (
      filters.selectedClaimForClaimNumber === null &&
      filters.selectedClaimTimePeriod === CLAIM_TIME_PERIOD.ALL &&
      filters.selectedMembers.length === 1 &&
      filters.selectedMembers.includes(userInfo.memberId) &&
      filters.selectedClaimType.VALUE === CLAIM_TYPES.ALL.VALUE &&
      filters.selectedCareEntity === "" &&
      filters.selectedPharmacy === "" &&
      filters.selectedProcedure === null &&
      filters.selectedDrugName === ""
    ) {
      return `Showing All Claims For: ${
        filters.selectedMembers?.length > 0
          ? filters.selectedMembers
              ?.map(
                (k) =>
                  members?.[k] &&
                  `${members[k]?.firstName} ${members[k]?.lastName}${k === userInfo.memberId ? " (Self)" : ""}`,
              )
              .filter(Boolean)
              .join(", ")
          : ""
      }`;
    } else {
      return `Showing Filtered Claims For:${
        filters.selectedMembers?.length > 0
          ? filters.selectedMembers
              ?.map(
                (k) =>
                  members?.[k] &&
                  `${members[k]?.firstName} ${members[k]?.lastName}${k === userInfo.memberId ? " (Self)" : ""}`,
              )
              .filter(Boolean)
              .join(", ")
          : ""
      }`;
    }
  };

  const getClaimNmberValue = (
    claim: CareHistoryClaimsResponseType | string | ClaimsProcedure,
  ): string => (claim as CareHistoryClaimsResponseType).revClaimId;

  const getCareEntityValue = (
    claim: CareHistoryClaimsResponseType | string | ClaimsProcedure,
  ): string => claim as string;

  const getProcedureValue = (
    claim: ClaimsProcedure | CareHistoryClaimsResponseType | string,
  ): string =>
    `${(claim as ClaimsProcedure).procedureDesc} - ${(claim as ClaimsProcedure).procedureCode}`;

  const getPharmacyValue = (
    claim: CareHistoryClaimsResponseType | string | ClaimsProcedure,
  ): string => claim as string;

  const getDrugNameValue = (
    claim: CareHistoryClaimsResponseType | string | ClaimsProcedure,
  ): string => claim as string;

  const filteredClaims = () => {
    if (filters?.selectedClaimForClaimNumber) {
      return filterByClaimNumber(
        claimsData ?? [],
        filters.selectedClaimForClaimNumber?.revClaimId,
      );
    } else {
      return filterByAdvancedSearch(
        config,
        claimsData ?? [],
        filters,
        claimsFromDate ?? undefined,
        claimsToDate ?? undefined,
      );
    }
  };

  const resetAdvancedFilter = () => {
    setFilters({
      ...filters,
      selectedFilterOption: CLAIMS_FILTER_OPTIONS.CLAIM_NUMBER.VALUE,
      selectedClaimTimePeriod: CLAIM_TIME_PERIOD.ALL,
      selectedMembers: getAllPHIVisibleMbrID(),
      selectedClaimType: CLAIM_TYPES.ALL,
      selectedCareEntity: "",
      selectedProcedure: null,
      selectedPharmacy: "",
      selectedDrugName: "",
      selectedDateRange: {
        fromDate: null,
        toDate: null,
      },
      sortClaimsBy: {
        LABEL: CLAIMS_SORT_BY.DATE_OF_SERVICE_NEW_TO_OLD.LABEL,
        VALUE: CLAIMS_SORT_BY.DATE_OF_SERVICE_NEW_TO_OLD.VALUE,
      },
    });
  };

  const resetDefaultFilter = () => {
    setFilters({
      ...filters,
      selectedFilterOption: CLAIMS_FILTER_OPTIONS.ADVANCED_SEARCH.VALUE,
      selectedClaimForClaimNumber: null,
    });
  };

  const formatYAxis = (tick: number): string => {
    return tick >= 1000 ? `$${tick / 1000}K` : `$${tick.toString()}`;
  };

  const openFilterSection = () => {
    if (
      filters?.selectedFilterOption === CLAIMS_FILTER_OPTIONS.CLAIM_NUMBER.VALUE
    ) {
      resetAdvancedFilter();
      setShowFilterSideBar(true);
    } else {
      resetDefaultFilter();
      setShowFilterSideBar(true);
    }
  };

  const graphMbrId =
    filters?.selectedMembers?.length === 0
      ? getAllPHIVisibleMbrID()
      : filters?.selectedMembers || [];

  return (
    <PageContainer
      title={`${featureName} ${userInfo?.planCoverageInfo?.length === 1 ? `(${activeCoverage.claimCoverageCodeDesc})` : ""}`}
    >
      <Tabs defaultValue="history" className="tabs">
        <div className="tabs-list-container hide-from-third-app">
          <TabsList className="tab-list">
            <TabsTrigger value="history" className="tabs-trigger">
              History
            </TabsTrigger>
            {/* <TabsTrigger value="place" className="tabs-trigger">
              Places
            </TabsTrigger> */}
          </TabsList>
        </div>
      </Tabs>
      <div className="hide-from-third-app m-4 flex items-center">
        <Title as="h2" className="font-pops text-sm font-medium">
          {selectedPeriod === CARE_HISTORY_PERIOD_FLAG.MONTHLY
            ? config?.monthlyGraph?.graphTitle?.toUpperCase()
            : config?.yearlyGraph?.graphTitle?.toUpperCase()}
        </Title>
        <div className="ml-4 w-[113px]">
          {config && (
            <SingleSelect
              data={[
                { displayName: config?.monthlyGraph?.label, value: "M" },
                { displayName: config?.yearlyGraph?.label, value: "Y" },
              ]}
              defaultOption={CARE_HISTORY_PERIOD_TYPE[0]}
              readValueFor="label"
              searchEnabled={false}
              onSelect={(item: OptionType) => {
                setSelectedPeriod(item?.value as string);
              }}
            />
          )}
        </div>
      </div>
      {showCareHistoryGraph ? (
        <div>
          {isGraphFetching && (
            <div className="hide-from-third-app">
              <GraphSkeletonLoader />
            </div>
          )}
          {isGraphError && <SomethingWentWrong />}
          {
            <div className="hide-from-third-app flex w-full justify-center">
              {graphData &&
              isGraphFetched &&
              isArray(graphData) &&
              graphData?.length > 0 ? (
                <ResponsiveContainer width="80%" height={300}>
                  <BarChart width={600} height={300} data={graphData}>
                    <CartesianGrid horizontal={true} vertical={false} />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={formatYAxis} />
                    {graphData && (
                      <Legend
                        verticalAlign="top"
                        payload={getLegendItems(
                          userInfo.memberId,
                          members,
                          graphMbrId,
                          graphData?.[0],
                        )}
                      />
                    )}
                    {graphMbrId?.map((key) => (
                      <Bar
                        key={key}
                        dataKey={`${filters?.selectedClaimType?.VALUE?.toLowerCase()}OopPaid${
                          key
                        }`}
                        radius={[4, 4, 0, 0]}
                        stackId="a"
                      >
                        {graphData?.map((entry, index) => {
                          return (
                            <Cell
                              key={`${key}-${index}`}
                              fill={entry[`color${key}`] as string}
                            />
                          );
                        })}
                      </Bar>
                    ))}
                    {/* Show family bar only when memberList contains employee's memberId */}
                    {graphMbrId?.length === 1 &&
                      graphMbrId?.[0] === userInfo.memberId && (
                        <Bar
                          dataKey={`${filters?.selectedClaimType?.VALUE.toLowerCase()}OopPaidFamily`}
                          radius={[4, 4, 0, 0]}
                          stackId="a"
                        >
                          {graphData?.map((entry, index) => (
                            <Cell
                              key={`family-${index}`}
                              fill={entry?.colorFamily}
                            />
                          ))}
                        </Bar>
                      )}
                  </BarChart>
                </ResponsiveContainer>
              ) : isGraphFetched ? (
                <NoDataAvailable
                  message="There is currently no graph data to display."
                  icon={ChartColumn}
                />
              ) : null}
            </div>
          }
          {/*Border between graph and spending details*/}
          <hr className="hide-from-third-app my-10 border-[7px] border-gray-100" />
        </div>
      ) : (
        <></>
      )}
      {isClaimsFetched && isArray(claimsData) && claimsData?.length > 0 && (
        <div className="hide-from-third-app">
          <Title as="h2" className="mb-4 text-center text-[22px]">
            SPENDING DETAILS
          </Title>
          <div className="m-4 flex flex-col items-center justify-between gap-5 md:flex-row">
            <div>
              <Title as="h3" className="text-md text-center md:text-start">
                {renderClaimsLabel()}
              </Title>
            </div>
            <div className="flex gap-2 sm:gap-4">
              {!isClaimsError && isClaimsFetched && (
                <Button
                  className="min-w-32 hover:bg-primary-selected"
                  onClick={onDownloadClaims}
                  disabled={
                    isDownloadClaimsPending || !claimsFromDate || !claimsToDate
                  }
                  data-testid={
                    IDENTIFIERS.CARE_AND_CLAIMS_HISTORY.DOWNLOAD_CLAIMS_BUTTON
                  }
                >
                  {isDownloadClaimsPending ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    "Download Claims"
                  )}
                </Button>
              )}
              {activeCoverage?.claimCoverageCode !==
                COVERAGE_CODE.ORTHODONTIA && (
                <Button
                  variant="default"
                  className="hover:bg-primary-selected"
                  onClick={openFilterSection}
                >
                  Filter & Sort
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      {isClaimsFetching && <ClaimsSkeletonLoader />}
      {isClaimsError && <SomethingWentWrong />}
      {claimsData &&
      isClaimsFetched &&
      isArray(claimsData) &&
      claimsData?.length > 0 ? (
        <VisitSummary
          groupedClaims={filteredClaims()}
          members={members}
          onVisitSummaryClick={onVisitSummaryClick}
        />
      ) : isClaimsFetched ? (
        <NoDataAvailable
          message="There is currently no claims data to display."
          icon={ReceiptText}
        />
      ) : null}
      {members && visitDetailsSideBar?.selectedVisitSummary && (
        <VisitDetailsModal
          isOpen={visitDetailsSideBar?.isOpen}
          visitDetail={visitDetailsSideBar?.selectedVisitSummary}
          appealClaimUrl={config?.url}
          onClose={onVisitDetailsSideBarClose}
          onEobError={() => setEobError(true)}
        />
      )}
      <AlertDialog
        open={Boolean(downloadClaimsErrorMsg)}
        onOpenChange={(open) => {
          if (!open) setDownloadClaimsErrorMsg(null);
        }}
        logTag={LogTags.MODULE_ACCESS}
        logCategory={LogCategories.CARE_AND_CLAIMS_HISTORY}
        logSubCategory={LogSubCategories.CARE_HISTORY_VISIT_DETAILS}
        logDialogName="Download Claims Error"
      >
        <AlertMessageDialog
          message={{
            title: "Download Failed",
            body:
              downloadClaimsErrorMsg ??
              "Failed to download claims. Please try again.",
            confirmBtnLabel: "OK",
            icon: CircleX,
            iconClassName: "text-red-700 bg-red-100",
          }}
          onClose={() => setDownloadClaimsErrorMsg(null)}
        />
      </AlertDialog>
      <AlertDialog
        open={eobError}
        onOpenChange={setEobError}
        logTag={LogTags.MODULE_ACCESS}
        logCategory={LogCategories.CARE_AND_CLAIMS_HISTORY}
        logSubCategory={LogSubCategories.CARE_HISTORY_VISIT_DETAILS}
        logDialogName="EOB Acknowledge"
      >
        <AlertMessageDialog
          message={{
            title: "Something Went Wrong",
            body: `${EOB_VERBIAGE.EOB_PENDING} Please try again later.`,
            confirmBtnLabel: "OK",
            icon: CircleX,
            iconClassName: "text-red-700 bg-red-100",
          }}
          testId={IDENTIFIERS.VIEW_EOB.ACKNOWLEDGE_BUTTON}
          onClose={() => setEobError(false)}
        />
      </AlertDialog>
      {showFilterSideBar ? (
        <RightSidebar
          title="Filter & Sort"
          isOpen={showFilterSideBar}
          onClose={onFilterSideBarClose}
        >
          <RadioGroup className="p-4" defaultValue="claims-number">
            <div
              className={`${
                filters?.selectedFilterOption ===
                CLAIMS_FILTER_OPTIONS.CLAIM_NUMBER.VALUE
                  ? "mb-2"
                  : "mb-5"
              }`}
            >
              <Collapsible
                isOpen={
                  filters?.selectedFilterOption ===
                  CLAIMS_FILTER_OPTIONS.CLAIM_NUMBER.VALUE
                }
                header={
                  /* 2.1.1 Keyboard — A: pointer-only convenience wrapper so a click
                      anywhere in the filter header resets it. It is not itself a control —
                      the RadioGroupItem inside is the focusable element, and activating
                      that radio by keyboard fires a click that bubbles here. Marked
                      presentational so it is not announced as an extra control. */
                  <div
                    role="presentation"
                    onClick={resetAdvancedFilter}
                    className="cursor-pointer select-none"
                  >
                    <label className="flex items-center space-x-3">
                      <RadioGroupItem
                        // type="radio"
                        // name="Advanced Search"
                        value="claims-number"
                        checked={
                          filters?.selectedFilterOption ===
                          CLAIMS_FILTER_OPTIONS.CLAIM_NUMBER.VALUE
                        }
                        className="size-4"
                      />
                      <span className="font-pops text-medium font-medium text-foreground">
                        {CLAIMS_FILTER_OPTIONS.CLAIM_NUMBER.LABEL}
                      </span>
                    </label>
                  </div>
                }
              >
                {claimsData && (
                  <AutoComplete<CareHistoryClaimsResponseType, ClaimsProcedure>
                    label={CLAIMS_FILTER_OPTIONS.CLAIM_NUMBER.VALUE}
                    data={claimsData?.flatMap((group) => group?.historyList)}
                    searchFunction={searchClaimNumbers}
                    onSelect={onClaimNumberSelect}
                    renderItem={renderClaimNumbers}
                    getItemValue={getClaimNmberValue}
                    defaultValue={filters?.selectedClaimForClaimNumber}
                    placeholder="Search"
                    onChange={(input: string) => {
                      if (input.length === 0) {
                        setFilters({
                          ...filters,
                          selectedClaimForClaimNumber: null,
                        });
                      }
                    }}
                  />
                )}
              </Collapsible>
            </div>
            <Collapsible
              isOpen={
                filters?.selectedFilterOption ===
                CLAIMS_FILTER_OPTIONS.ADVANCED_SEARCH.VALUE
              }
              header={
                /* 2.1.1 Keyboard — A: pointer-only convenience wrapper around the
                    RadioGroupItem below, which is the real focusable control. */
                <div role="presentation" onClick={resetDefaultFilter}>
                  <label className="flex items-center space-x-3">
                    <RadioGroupItem
                      // type="radio"
                      // name="Advanced Search"
                      value="advanced-search"
                      checked={
                        filters?.selectedFilterOption ===
                        CLAIMS_FILTER_OPTIONS.ADVANCED_SEARCH.VALUE
                      }
                      className="size-4"
                    />
                    <span className="font-pops text-medium text-foreground">
                      {CLAIMS_FILTER_OPTIONS.ADVANCED_SEARCH.LABEL}
                    </span>
                  </label>
                </div>
              }
            >
              <Label>Claim Time Period</Label>
              <Dropdown<ClaimFilterDropDownType>
                data={
                  config?.yearlyGraph?.label?.startsWith("Plan")
                    ? PLAN_YEAR_DROPDOWN_OPTIONS
                    : ACCUMULATOR_YEAR_DROPDOWN_OPTIONS
                }
                value={
                  (config?.yearlyGraph?.label?.startsWith("Plan")
                    ? PLAN_YEAR_DROPDOWN_OPTIONS
                    : ACCUMULATOR_YEAR_DROPDOWN_OPTIONS
                  ).find(
                    (opt) => opt.VALUE === filters?.selectedClaimTimePeriod,
                  ) ??
                  (config?.yearlyGraph?.label?.startsWith("Plan")
                    ? PLAN_YEAR_DROPDOWN_OPTIONS[0]
                    : ACCUMULATOR_YEAR_DROPDOWN_OPTIONS[0])
                }
                multiple={false}
                renderItem={(item) => <span>{item.LABEL}</span>}
                getItemValue={(item) => String(item.VALUE)}
                onChange={(selected) => {
                  const value = (selected as ClaimFilterDropDownType).VALUE;
                  const isDateOfService = value === "Date of Service";
                  setFilters((prev) => {
                    const normalized = normalizeCareHistoryServiceDateRange({
                      claimsFromDate,
                      claimsToDate,
                      planStartDate: config?.planStartDate,
                      planEndDate: config?.planEndDate,
                      fromDate: prev?.selectedDateRange?.fromDate ?? null,
                      toDate: prev?.selectedDateRange?.toDate ?? null,
                    });

                    return {
                      ...prev,
                      selectedClaimTimePeriod: value,
                      selectedDateRange: isDateOfService
                        ? {
                            fromDate: normalized.uiFrom ?? null,
                            toDate: normalized.uiTo,
                          }
                        : {
                            fromDate: null,
                            toDate: null,
                          },
                    };
                  });
                }}
              />
              {filters.selectedClaimTimePeriod === "Date of Service" && (
                <div className="my-3 flex flex-col gap-2 px-3.5">
                  <div className="flex gap-4">
                    <InputDate
                      id="service-start-date"
                      label="Service Start Date"
                      placeholder="Select start date"
                      className="h-9 border-solid border-gray-200 bg-white px-3 py-2 text-sm focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:outline-none"
                      value={fromDate}
                      minDate={serviceDateRange.periodStart}
                      maxDate={serviceStartMax}
                      onChange={(newDate) =>
                        setFilters((prev) => ({
                          ...prev,
                          selectedDateRange: {
                            ...(prev?.selectedDateRange ?? {
                              fromDate: null,
                              toDate: null,
                            }),
                            fromDate: newDate ?? null,
                          },
                        }))
                      }
                      onClose={() => setPreventClose(true)}
                    />
                    <InputDate
                      id="service-end-date"
                      label="Service End Date"
                      placeholder="Select end date"
                      className="h-9 border-solid border-gray-200 bg-white px-3 py-2 text-sm focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:outline-none"
                      value={toDate}
                      minDate={
                        fromDate ?? serviceDateRange.periodStart ?? toDate
                      }
                      maxDate={effectiveMaxDate}
                      onChange={(newDate) =>
                        setFilters((prev) => ({
                          ...prev,
                          selectedDateRange: {
                            ...(prev?.selectedDateRange ?? {
                              fromDate: null,
                              toDate: null,
                            }),
                            toDate: newDate ?? null,
                          },
                        }))
                      }
                      onClose={() => setPreventClose(true)}
                    />
                  </div>
                </div>
              )}
              <Label>Included Members</Label>
              <Dropdown<Relation>
                lookupKey="memberId"
                data={Object.values(members)}
                value={[
                  ...dependents,
                  { ...members[userInfo.memberId] },
                ].filter((m) =>
                  filters?.selectedMembers?.includes(String(m.memberId)),
                )}
                multiple={true}
                renderItem={(item) => (
                  <span>
                    {item?.firstName} {item?.lastName}{" "}
                    {item?.hipaaProtected &&
                    item?.memberId !== userInfo.memberId
                      ? "(HIPAA Protected)"
                      : null}
                    {item?.memberId === userInfo?.memberId ? "(Self)" : null}
                  </span>
                )}
                isItemDisabled={(item) =>
                  !getDisabledStatus(
                    item,
                    authorizationAccessList as DataAccessListItem[],
                    userInfo.memberId,
                    userInfo.relationship,
                  ) || item?.memberId === userInfo.memberId
                    ? false
                    : true
                }
                getItemValue={({ memberId, firstName, lastName }) => {
                  const fullName = `${firstName} ${lastName}`;
                  return memberId === userInfo?.memberId
                    ? `${fullName} (Self)`
                    : fullName;
                }}
                onChange={(selected) => {
                  const selectedMemberIds: string[] = (
                    selected as Relation[]
                  ).map((member) => member?.memberId);
                  hasUserSelectedMembers.current = true;
                  setFilters({
                    ...filters,
                    selectedMembers: selectedMemberIds,
                  });
                }}
              />
              {activeCoverage?.claimCoverageCode === COVERAGE_CODE.MEDICAL && (
                <>
                  <Label>Type(Medical/Pharmacy)</Label>
                  <Dropdown<ClaimFilterDropDownType>
                    data={CLAIM_TYPE_DROPDOWN_OPTIONS}
                    value={filters?.selectedClaimType}
                    multiple={false}
                    renderItem={(item) => <span>{item.LABEL}</span>}
                    getItemValue={(item) => String(item.LABEL)}
                    onChange={(selected) =>
                      setFilters({
                        ...filters,
                        selectedClaimType: selected as ClaimFilterDropDownType,
                      })
                    }
                  />
                </>
              )}
              {claimsData && (
                <AutoComplete<CareHistoryClaimsResponseType, ClaimsProcedure>
                  label={`Facility/Provider${activeCoverage?.claimCoverageCode === COVERAGE_CODE.MEDICAL ? "/Prescriber" : ""}`}
                  data={claimsData?.flatMap((group) => group?.historyList)}
                  searchFunction={searchCareEntity}
                  onSelect={onCareEntitySelect}
                  renderItem={renderCareEntity}
                  getItemValue={getCareEntityValue}
                  defaultValue={filters?.selectedCareEntity}
                  placeholder="Search"
                  onChange={(input: string) => {
                    if (input.length === 0) {
                      setFilters({ ...filters, selectedCareEntity: "" });
                    }
                  }}
                />
              )}
              {claimsData && (
                <AutoComplete<CareHistoryClaimsResponseType, ClaimsProcedure>
                  label="Procedure"
                  data={claimsData?.flatMap((group) => group?.historyList)}
                  searchFunction={searchProcedure}
                  onSelect={onProcedureSelect}
                  renderItem={renderProcedure}
                  getItemValue={getProcedureValue}
                  defaultValue={filters?.selectedProcedure}
                  placeholder="Search"
                  onChange={(input: string) => {
                    if (input.length === 0) {
                      setFilters({ ...filters, selectedProcedure: null });
                    }
                  }}
                />
              )}
              {claimsData &&
                activeCoverage?.claimCoverageCode === COVERAGE_CODE.MEDICAL && (
                  <AutoComplete<CareHistoryClaimsResponseType, ClaimsProcedure>
                    label="Pharmacy"
                    data={claimsData?.flatMap((group) => group?.historyList)}
                    searchFunction={searchPharmacy}
                    onSelect={onPharmacySelect}
                    renderItem={renderPharmacy}
                    getItemValue={getPharmacyValue}
                    defaultValue={filters?.selectedPharmacy}
                    placeholder="Search"
                    onChange={(input: string) => {
                      if (input.length === 0) {
                        setFilters({ ...filters, selectedPharmacy: "" });
                      }
                    }}
                  />
                )}
              {claimsData &&
                activeCoverage?.claimCoverageCode === COVERAGE_CODE.MEDICAL && (
                  <AutoComplete<CareHistoryClaimsResponseType, ClaimsProcedure>
                    label="Drug Name"
                    data={claimsData?.flatMap((group) => group.historyList)}
                    searchFunction={searchDrugName}
                    onSelect={onDrugNameSelect}
                    renderItem={renderDrugName}
                    getItemValue={getDrugNameValue}
                    defaultValue={filters?.selectedDrugName}
                    placeholder="Search"
                    onChange={(input: string) => {
                      if (input.length === 0) {
                        setFilters({ ...filters, selectedDrugName: "" });
                      }
                    }}
                  />
                )}

              <Label>Sort By</Label>
              <Dropdown<ClaimFilterDropDownType>
                data={CLAIMS_SORT_OPTIONS}
                value={filters.sortClaimsBy}
                multiple={false}
                renderItem={(item) => <span>{item.LABEL}</span>}
                getItemValue={(item) => String(item.LABEL)}
                onChange={(selected) =>
                  setFilters({
                    ...filters,
                    sortClaimsBy: selected as ClaimFilterDropDownType,
                  })
                }
              />
            </Collapsible>
          </RadioGroup>
        </RightSidebar>
      ) : null}
    </PageContainer>
  );
};

export default CareHistory;
